import { createApp, h } from 'vue';
import AssistenteIAChat from '@/components/AssistenteIAChat.vue';
import AdminCorrecoes from '@/components/AdminCorrecoes.vue';
import type { ContextoAssistente } from '@/types/api';

// Auto-mount: o MaisVidas legado coloca uma <div id="assistenteia-mount" data-...> no layout.
// Os atributos data-* viram o ContextoAssistente. apiBaseUrl pode ser:
//   - data-api-base-url no mount, OU
//   - infer pelo location.origin (mesma origem em producao)

interface MountConfig {
  apiBaseUrl: string;
  contexto: ContextoAssistente;
}

function lerInt(el: HTMLElement, attr: string): number | null {
  const v = el.dataset[attr];
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function lerCsv(el: HTMLElement, attr: string): number[] {
  const v = el.dataset[attr];
  if (!v) return [];
  return v.split(',').map(s => Number(s.trim())).filter(n => Number.isFinite(n));
}

function lerBool(el: HTMLElement, attr: string): boolean {
  const v = el.dataset[attr];
  return v === 'true' || v === '1';
}

function configFromMount(el: HTMLElement): MountConfig {
  const apiBaseUrl = el.dataset.apiBaseUrl
    ?? (typeof window !== 'undefined' ? window.location.origin : '');

  const contexto: ContextoAssistente = {
    baseCliente:     el.dataset.baseCliente     ?? '',
    sistema:         el.dataset.sistema         ?? 'MaisVidas',
    plataforma:      (el.dataset.plataforma as ContextoAssistente['plataforma']) ?? 'Web',
    usuarioID:       lerInt(el, 'usuarioId') ?? 0,
    usuarioNome:     el.dataset.usuarioNome ?? undefined,
    usuarioTipoID:   lerInt(el, 'usuarioTipoId') ?? undefined,
    contratanteID:   lerInt(el, 'contratanteId'),
    perfisAtivos:    lerCsv(el, 'perfisAtivos'),
    ehAdmin:         lerBool(el, 'ehAdmin'),
    controllerAtual: el.dataset.controllerAtual || null,
    acaoAtual:       el.dataset.acaoAtual || null,
    menuIDAtual:     lerInt(el, 'menuIdAtual'),
    registroIDAtual: lerInt(el, 'registroIdAtual')
  };

  return { apiBaseUrl, contexto };
}

function montar(el: HTMLElement) {
  const cfg = configFromMount(el);
  const app = createApp({
    render: () => h(AssistenteIAChat, { apiBaseUrl: cfg.apiBaseUrl, contexto: cfg.contexto })
  });
  app.mount(el);
}

function montarAdmin(el: HTMLElement) {
  // Admin lê params de query (?tipoid=-7&userid=123) ou data attributes.
  // Em URL: https://maisvidasia.hopesolution.com.br/admin.html?tipoid=-7&userid=123
  const sp = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');

  const apiBaseUrl = el.dataset.apiBaseUrl
    ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const tipoIdRaw = el.dataset.usuarioTipoId ?? sp.get('tipoid') ?? '';
  const userIdRaw = el.dataset.usuarioId      ?? sp.get('userid') ?? '';

  const usuarioTipoID = Number(tipoIdRaw);
  const revisorUsuarioID = Number(userIdRaw);

  if (!Number.isFinite(usuarioTipoID) || !Number.isFinite(revisorUsuarioID)) {
    el.innerHTML = '<div style="padding:24px;color:#b91c1c;font-family:sans-serif">' +
      'Acesso ao painel de admin requer parâmetros <code>?tipoid=-7&amp;userid=SEU_ID</code> na URL ' +
      'ou data attributes <code>data-usuario-tipo-id</code> + <code>data-usuario-id</code>.' +
      '</div>';
    return;
  }

  const app = createApp({
    render: () => h(AdminCorrecoes, {
      apiBaseUrl,
      usuarioTipoID,
      revisorUsuarioID
    })
  });
  app.mount(el);
}

function bootstrap() {
  const mountChat = document.getElementById('assistenteia-mount');
  if (mountChat) montar(mountChat as HTMLElement);

  const mountAdmin = document.getElementById('assistenteia-admin-mount');
  if (mountAdmin) montarAdmin(mountAdmin as HTMLElement);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

// Exposicao manual (caso o legado prefira instanciar via JS em vez do auto-mount)
declare global {
  interface Window {
    AssistenteIA?: { mount: (el: HTMLElement) => void };
  }
}
if (typeof window !== 'undefined') {
  window.AssistenteIA = { mount: montar };
}
